// RPT1-F4 (docs/build-briefs/RPT1-F4-my-tips-on-the-floor.md; DEC-285, DEC-268,
// DEC-281): "My Tips", the floor sheet opened from the account menu. It reads
// two existing routes and nothing else: GET .../reports/context for the
// business date, cutoff and time zone, and GET .../reports/tips-by-employee,
// unchanged. The pure half (range math, own-row pick, view model) is
// ui/src/myTipsRange.ts; this module is the DOM and the two reads.
//
// Touch only: no key or hover handler anywhere here (check:touch-only). The
// sheet closes on Done and on a tap of its backdrop, and main.ts closes it on
// sign-out through closeAllFloorSheets() (closeMyTips), which also drops any
// read still in flight so a late answer never lands on the next employee.
//
// A failed read never draws a zero that looks real: the previous total stays,
// under its own range label, and the chip that produced it is pressed again.

import { getReportContext, getTipsByEmployeeReport, type ReportContextDto } from "./api.ts";
import {
  MY_TIPS_CHIPS,
  buildMyTipsView,
  myTipsRequestRange,
  tenantCalendarDate,
  type MyTipsChip,
  type MyTipsView,
} from "./myTipsRange.ts";
import { addDays, cutoffText, formatMoney } from "./reportFormat.ts";
import type { Session } from "./session.ts";

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) {
    throw new Error(`expected element for selector "${selector}"`);
  }
  return element;
}

const backdropEl = mustFind<HTMLElement>("#my-tips-backdrop");
const doneButton = mustFind<HTMLButtonElement>("#my-tips-done");
const chipsEl = mustFind<HTMLElement>("#my-tips-chips");
const rangeEl = mustFind<HTMLElement>("#my-tips-range");
const totalEl = mustFind<HTMLElement>("#my-tips-total");
const noteEl = mustFind<HTMLElement>("#my-tips-note");
const nightNoteEl = mustFind<HTMLElement>("#my-tips-night-note");
const listTitleEl = mustFind<HTMLElement>("#my-tips-list-title");
const daysEl = mustFind<HTMLElement>("#my-tips-days");
const emptyEl = mustFind<HTMLElement>("#my-tips-empty");
const emptyTitleEl = mustFind<HTMLElement>("#my-tips-empty-title");
const emptyBodyEl = mustFind<HTMLElement>("#my-tips-empty-body");
const fineEl = mustFind<HTMLElement>("#my-tips-fine");

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function weekdayName(dateIso: string): string {
  const [y, m, d] = dateIso.split("-").map(Number) as [number, number, number];
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()] ?? "";
}

function shortDate(dateIso: string): string {
  const [, m, d] = dateIso.split("-").map(Number) as [number, number, number];
  return `${MONTHS[m - 1]} ${d}`;
}

interface MyTipsState {
  session: Session | undefined;
  context: ReportContextDto | undefined;
  /** The chip whose figures are on screen (undefined before the first read). */
  loadedChip: MyTipsChip | undefined;
  /** The chip last tapped, whose read may still be in flight. */
  chip: MyTipsChip;
}

let state: MyTipsState = {
  session: undefined,
  context: undefined,
  loadedChip: undefined,
  chip: "today",
};
/** Bumped on every open and close, so an answer that arrives after either is dropped. */
let generation = 0;
/** Bumped on every read, so only the newest chip tap is applied. */
let readSeq = 0;
let notify: (message: string) => void = () => undefined;

const chipButtons = new Map<MyTipsChip, HTMLButtonElement>();

function buildChips(): void {
  for (const { id, label } of MY_TIPS_CHIPS) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "mytips-chip";
    button.dataset["testid"] = `my-tips-chip-${id}`;
    button.textContent = label;
    button.setAttribute("aria-pressed", "false");
    button.addEventListener("click", () => void selectChip(id));
    chipsEl.append(button);
    chipButtons.set(id, button);
  }
}

function pressChip(chip: MyTipsChip | undefined): void {
  for (const [id, button] of chipButtons) {
    button.setAttribute("aria-pressed", String(id === chip));
  }
}

/** Wires the sheet once. `toast` shows the shipped error toast (main.ts owns it). */
export function initMyTips(deps: { toast: (message: string) => void }): void {
  notify = deps.toast;
  buildChips();
  doneButton.addEventListener("click", () => closeMyTips());
  // A tap on the dimmed area beside the panel, never on the panel itself.
  backdropEl.addEventListener("click", (event) => {
    if (event.target === backdropEl) closeMyTips();
  });
}

function clearChildren(el: HTMLElement): void {
  while (el.firstChild !== null) el.removeChild(el.firstChild);
}

function resetBody(): void {
  rangeEl.textContent = "";
  totalEl.textContent = "-";
  totalEl.classList.remove("zero");
  noteEl.textContent = "Loading your tips.";
  nightNoteEl.hidden = true;
  listTitleEl.hidden = true;
  daysEl.hidden = true;
  clearChildren(daysEl);
  emptyEl.hidden = true;
  fineEl.textContent = "Tips count on the night you worked.";
}

export function openMyTips(session: Session): void {
  generation += 1;
  readSeq += 1;
  state = { session, context: undefined, loadedChip: undefined, chip: "today" };
  resetBody();
  pressChip("today");
  backdropEl.hidden = false;
  doneButton.focus();
  void load("today");
}

/** Hides the sheet and forgets the employee's figures. Safe to call when it is
 * not open, which is what closeAllFloorSheets() relies on. */
export function closeMyTips(): void {
  generation += 1;
  readSeq += 1;
  backdropEl.hidden = true;
  state = { session: undefined, context: undefined, loadedChip: undefined, chip: "today" };
  resetBody();
}

async function selectChip(chip: MyTipsChip): Promise<void> {
  if (state.session === undefined) return;
  state.chip = chip;
  pressChip(chip);
  await load(chip);
}

function failRead(chip: MyTipsChip): void {
  notify("Could not load your tips. Try again.");
  if (state.loadedChip !== undefined) {
    // Keep the previous total under its own label; press its chip again.
    pressChip(state.loadedChip);
    state.chip = state.loadedChip;
    return;
  }
  // Nothing was ever loaded: say so, never a zero.
  state.chip = chip;
  totalEl.textContent = "-";
  totalEl.classList.remove("zero");
  noteEl.textContent = "Could not load your tips. Tap a range to try again.";
}

async function load(chip: MyTipsChip): Promise<void> {
  const session = state.session;
  if (session === undefined) return;
  const gen = generation;
  readSeq += 1;
  const seq = readSeq;
  const current = (): boolean => gen === generation && seq === readSeq;

  // "Today" is the server's business date, never the tablet clock.
  let context = state.context;
  if (context === undefined) {
    const result = await getReportContext(session.apiBaseUrl, session.tenantId);
    if (!current()) return;
    if (result.status !== 200 || typeof result.body.today !== "string") {
      failRead(chip);
      return;
    }
    context = {
      today: result.body.today,
      timezone: result.body.timezone,
      cutoffLocal: result.body.cutoffLocal,
    };
    state.context = context;
  }
  const { from, to } = myTipsRequestRange(chip, context.today);
  const result = await getTipsByEmployeeReport(session.apiBaseUrl, session.tenantId, from, to);
  if (!current()) return;
  if (result.status !== 200 || !Array.isArray(result.body.employees)) {
    failRead(chip);
    return;
  }
  // Only the signed-in user's own row is read (buildMyTipsView): a manager's
  // report is everyone:true and its top-level total is the whole location's.
  const view = buildMyTipsView({
    chip,
    today: context.today,
    userId: session.userId,
    report: result.body,
  });
  state.loadedChip = chip;
  render(view, context);
}

function rangeLabel(view: MyTipsView, today: string): string {
  const name = MY_TIPS_CHIPS.find((c) => c.id === view.chip)?.label ?? "";
  if (view.from === view.to) {
    const day = view.chip === "today" ? `${weekdayName(view.from)} night` : weekdayName(view.from);
    return `${name}, ${day}, ${shortDate(view.from)}`;
  }
  void today;
  return `${name}, ${shortDate(view.from)} to ${shortDate(view.to)}`;
}

function el(tag: string, className: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function render(view: MyTipsView, context: ReportContextDto): void {
  const cutoff = cutoffText(context.cutoffLocal);
  rangeEl.textContent = rangeLabel(view, context.today);
  totalEl.textContent = formatMoney(view.totalCents);
  totalEl.classList.toggle("zero", view.totalCents === 0);
  noteEl.textContent =
    view.totalCents === 0
      ? view.live
        ? "Nothing yet. Tips count when a tab you worked is paid."
        : "No tips in this range."
      : view.live
        ? `Still counting. Tonight is not final until ${cutoff}.`
        : "Final. These nights are closed.";

  // Wording only: it never chooses a date to read.
  const calendarToday = tenantCalendarDate(Date.now(), context.timezone);
  if (view.live && calendarToday !== context.today) {
    nightNoteEl.textContent =
      `It is past midnight. Tips from now still count on ${weekdayName(context.today)} night, ` +
      `until ${cutoff} ${weekdayName(addDays(context.today, 1))}.`;
    nightNoteEl.hidden = false;
  } else {
    nightNoteEl.hidden = true;
  }

  clearChildren(daysEl);
  emptyEl.hidden = !view.empty;
  daysEl.hidden = view.empty;
  listTitleEl.hidden = view.empty || view.rows.length < 2;
  if (view.empty) {
    emptyTitleEl.textContent = view.none
      ? "No earlier night with tips"
      : view.chip === "today"
        ? "No tips yet tonight"
        : "No tips in this range";
    emptyBodyEl.textContent = view.none
      ? "Your last 14 days have no tips."
      : "Tips show up here as soon as a tab you worked is paid.";
  } else {
    for (const row of view.rows) {
      const node = el("div", `mytips-day${row.cents === 0 ? " none" : ""}`);
      node.dataset["testid"] = "my-tips-day-row";
      node.dataset["date"] = row.date;
      if (row.cents > 0 && view.maxCents > 0) {
        const bar = el("div", "mytips-bar");
        bar.style.width = `${Math.round((row.cents / view.maxCents) * 100)}%`;
        node.append(bar);
      }
      const main = el("div", "mytips-day-main");
      const name = el("div", "mytips-day-name", weekdayName(row.date));
      if (row.live) {
        name.append(el("span", "mytips-tag live", "Tonight"));
      } else if (row.best) {
        name.append(el("span", "mytips-tag best", "Best night"));
      }
      const date = el(
        "div",
        "mytips-day-date",
        row.live
          ? `${shortDate(row.date)}, runs until ${cutoff} ${weekdayName(addDays(row.date, 1))}`
          : shortDate(row.date),
      );
      main.append(name, date);
      node.append(
        main,
        el("div", "mytips-day-amt", row.cents === 0 ? "No tips" : formatMoney(row.cents)),
      );
      daysEl.append(node);
    }
  }
  fineEl.textContent = `Tips count on the night you worked. A night ends at ${cutoff}, not at midnight.`;
}
